import { Link } from "react-router-dom";
import { ArrowRight, X, Check } from "lucide-react";
import { IMG } from "../data/content";
import Seo from "../components/Seo";

const CONSIDERATIONS = [
  "Land tenure and ownership",
  "Native Title rights and interests",
  "Protected area regulations",
  "Cultural authority and Traditional Owner permissions",
  "Environmental approvals",
  "Collection permits and licences",
  "Threatened species requirements",
  "Access agreements",
  "Benefit-sharing obligations",
];

const REGULATORY = [
  "State and Territory permits",
  "National park permits",
  "Vegetation management requirements",
  "Threatened species approvals",
  "Indigenous Land Use Agreements",
  "Native Title considerations",
  "Landholder permissions",
  "Commercial access agreements",
];

const RELATIONSHIPS = [
  "Respectful engagement",
  "Clear communication",
  "Transparency",
  "Shared benefits",
  "Long-term relationships",
  "Stewardship responsibilities",
];

const READINESS = [
  "Who are the Traditional Owners?",
  "Has cultural authority been recognised?",
  "Who owns or manages the land?",
  "Are permits required?",
  "Are there Native Title considerations?",
  "Are there threatened species restrictions?",
  "Have benefits and responsibilities been discussed?",
  "How will collection information be recorded and managed?",
  "Who will have access to the data?",
  "What happens after collection?",
];

export default function Permits() {
  return (
    <div data-testid="page-permits">
      <Seo
        title="Permits, Permissions and Cultural Authority"
        description="Native seed collection occurs within a complex landscape of legislation, land tenure, cultural authority and environmental responsibility. Begin with relationships, not equipment."
        path="/permits"
      />

      {/* Hero */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="stamp">Core to SeedKeepers</span>
              <span className="specimen-label">Permits, Permissions & Cultural Authority</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
              Collecting seed is about more than <em className="text-eucalypt">access.</em>
            </h1>
            <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              Native seed collection occurs within a complex landscape of
              legislation, land tenure, cultural authority and environmental
              responsibility.
            </p>
            <p className="mt-4 font-serif italic text-2xl md:text-3xl text-eucalypt leading-snug max-w-2xl">
              Successful seed collection begins with relationships and
              permissions — not collection equipment.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="polaroid">
              <img src="https://customer-assets.emergentagent.com/job_seedkeepers-au/artifacts/5a7d4pj9_seed%20collection%20permit.png" alt="Sample Australian Government seed collection permit on a wooden surface" className="w-full h-[440px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Considerations before collection */}
      <section className="border-b border-rule bg-parchment/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">Before seed is collected</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-12 max-w-3xl leading-tight">
            Consideration should be given to:
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-rule border border-rule" data-testid="considerations">
            {CONSIDERATIONS.map((c, i) => (
              <li key={c} className="bg-card p-6 md:p-7 flex gap-4 items-start min-h-[130px]" data-testid={`consideration-${i}`}>
                <span className="font-serif text-3xl text-ochre2 leading-none">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-serif text-lg md:text-xl leading-snug pt-1">{c}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Start With Country — contrast block */}
      <section className="border-b border-rule bg-bark text-chalk">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="specimen-label text-chalk/60 mb-4">Start With Country</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-4xl mb-16">
            The first question should not be what most projects ask. It should be a different question entirely.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-chalk/15" data-testid="start-with-country">
            <div className="bg-bark p-8 md:p-12">
              <div className="flex items-center gap-3 mb-5">
                <X size={20} className="text-terracotta" />
                <span className="specimen-label text-chalk/60">The common question</span>
              </div>
              <p className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-chalk/85">
                "Can we collect seed here?"
              </p>
            </div>
            <div className="bg-bark p-8 md:p-12 border-l border-chalk/15">
              <div className="flex items-center gap-3 mb-5">
                <Check size={20} className="text-ochre" />
                <span className="specimen-label text-ochre">The first question</span>
              </div>
              <p className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.1]">
                "Who speaks for this Country?"
              </p>
            </div>
          </div>

          <p className="mt-14 text-base md:text-lg text-chalk/80 leading-relaxed max-w-3xl">
            Understanding cultural authority and engaging Traditional Owners
            early helps reduce risk, strengthen relationships and improve
            restoration outcomes. Many restoration projects engage Traditional
            Owners after collection plans have already been developed.
            SeedKeepers encourages the opposite approach.
          </p>
          <p className="mt-6 font-serif italic text-2xl md:text-3xl text-ochre max-w-3xl">
            Engagement should occur at the beginning of the process, not at the end.
          </p>
        </div>
      </section>

      {/* Regulatory landscape */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="section-num mb-3">Understanding the Regulatory Landscape</p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
                Approval pathways vary by location and species.
              </h2>
              <p className="mt-8 text-base md:text-lg text-ink/85 leading-relaxed">
                Requirements vary significantly between jurisdictions and land
                tenures. Early planning is essential.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="border-y border-rule divide-y divide-rule" data-testid="regulatory-list">
                {REGULATORY.map((r, i) => (
                  <li key={r} className="py-5 grid grid-cols-12 gap-4 items-baseline" data-testid={`regulatory-${i}`}>
                    <span className="col-span-3 specimen-label text-eucalypt">{String(i + 1).padStart(2, "0")}</span>
                    <p className="col-span-9 font-serif text-xl md:text-2xl leading-snug">{r}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Beyond Compliance */}
      <section className="border-b border-rule bg-parchment/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">Beyond Compliance</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] max-w-3xl mb-8">
            Meeting legal requirements is important. But compliance alone does not create trust.
          </h2>
          <p className="text-base md:text-lg text-ink/85 leading-relaxed max-w-3xl mb-14">
            The strongest seed collection programs are built on:
          </p>

          <ul className="grid grid-cols-2 md:grid-cols-3 gap-px bg-rule border border-rule mb-14" data-testid="relationships">
            {RELATIONSHIPS.map((r, i) => (
              <li key={r} className="bg-card p-6 md:p-7 min-h-[120px] flex flex-col justify-between" data-testid={`relationship-${i}`}>
                <span className="specimen-label">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-serif text-lg md:text-xl leading-tight">{r}</p>
              </li>
            ))}
          </ul>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            <div className="border-l-2 border-rule pl-6">
              <p className="specimen-label text-muted mb-2">Legal</p>
              <p className="font-serif text-2xl md:text-3xl leading-snug">Permits may grant legal access.</p>
            </div>
            <div className="border-l-2 border-eucalypt pl-6">
              <p className="specimen-label text-eucalypt mb-2">Social</p>
              <p className="font-serif text-2xl md:text-3xl leading-snug">Relationships create social licence.</p>
            </div>
          </div>
          <p className="mt-8 font-serif italic text-2xl md:text-3xl text-eucalypt">Both matter.</p>
        </div>
      </section>

      {/* Collection Readiness Checklist */}
      <section className="border-b border-rule">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">Seed Collection Readiness</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] max-w-3xl mb-12">
            Before collecting seed, ask:
          </h2>

          <ol className="space-y-1 border-y border-rule" data-testid="readiness-checklist">
            {READINESS.map((q, i) => (
              <li key={q} className="py-5 border-b border-rule last:border-b-0 grid grid-cols-12 gap-4 items-baseline" data-testid={`readiness-${i}`}>
                <span className="col-span-2 md:col-span-1 font-mono text-xs tracking-[0.2em] text-ochre2">{String(i + 1).padStart(2, "0")}</span>
                <p className="col-span-10 md:col-span-11 font-serif text-xl md:text-2xl leading-snug">{q}</p>
              </li>
            ))}
          </ol>

          <p className="mt-14 font-serif italic text-2xl md:text-3xl text-muted leading-snug max-w-3xl">
            If these questions remain unanswered, the project may not yet be
            collection ready.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 text-center">
          <p className="section-num mb-8">Next</p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.15]">
            We help projects work through permits, permissions and cultural authority — before any seed is collected.
          </h2>
          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="permits-contact-cta"
              className="inline-flex items-center gap-3 bg-eucalypt text-chalk px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all"
            >
              Talk to us about collection readiness <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
